import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Tractor, HeartHandshake, FlaskConical, Package, Home as HomeIcon, Sprout, Cog, Check, ArrowRight, Play, X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { TrustBar } from "@/components/TrustBar";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { IMG, SEAL, SEAL_GOLD } from "@/data/images";
import { useImg } from "@/lib/useImg";
import { useSettings } from "@/context/SettingsContext";

const PILLARS = [
  { icon: Leaf, title: "Nature First", text: "We begin with pure, natural ingredients — exactly as nature intended them." },
  { icon: Tractor, title: "Our Farmers", text: "We partner with farmers who share our values and care for their land." },
  { icon: HeartHandshake, title: "Responsible Sourcing", text: "We source with respect — for people, communities and the planet." },
  { icon: FlaskConical, title: "Science & Quality", text: "We apply science-backed standards to protect purity and safety." },
  { icon: Package, title: "Thoughtful Packaging", text: "We package to protect freshness and nutritional integrity." },
  { icon: HomeIcon, title: "Your Family", text: "We deliver nutrition you can trust, to the people you love most." },
];

const EVIDENCE = [
  { icon: Sprout, q: "Sourcing", a: "Fair, long-term partnerships with farmers — pure, natural ingredients, and we know exactly where every one comes from." },
  { icon: FlaskConical, q: "Testing", a: "Every batch is verified by accredited third-party labs for purity, safety and nutritional integrity before it reaches you." },
  { icon: Cog, q: "Processing", a: "Hygienic, carefully controlled processing preserves natural goodness — no artificial additives, preservatives or colours." },
  { icon: Package, q: "Packaging", a: "Food-safe packaging that protects freshness, with a footprint we keep making lighter." },
];

const COMMITMENTS = [
  "We source responsibly.",
  "We test rigorously.",
  "We craft carefully.",
  "We package mindfully.",
  "We deliver honestly.",
];

export default function Experience() {
  const img = useImg();
  const settings = useSettings();
  const [showVideo, setShowVideo] = useState(false);
  const videoUrl = settings.family_video || "/media/golden-rule.mp4";

  return (
    <div data-testid="experience-page">
      {/* 1 — Make them feel something */}
      <PageHero
        tall
        eyebrow="The Nutribest Experience"
        lines={["More than nutrition.", "A promise you can trust."]}
        sub="Every product carries our name — and with it, a promise we make to your family as if it were our own."
        image={img("heroExperience")}
      />

      {/* 2 — Show why it matters */}
      <section className="py-20 sm:py-28 bg-creamdark/40">
        <div className="container-nb grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <button type="button" onClick={() => setShowVideo(true)} data-testid="family-video-trigger" className="group relative block w-full overflow-hidden rounded-2xl shadow-xl shadow-forest/10">
              <img src={img("familyVideoPoster")} alt="The Nutribest family sharing a wholesome meal together" className="w-full h-80 lg:h-[460px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-forestdeep/25 group-hover:bg-forestdeep/15 transition-colors duration-500" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-cream/95 text-forest shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <span className="absolute inset-0 rounded-full bg-cream/50 animate-ping" />
                  <Play size={26} className="relative ml-1 fill-forest" />
                </span>
              </span>
              <span className="absolute bottom-4 left-5 text-cream text-[11px] font-semibold uppercase tracking-[0.18em] drop-shadow-lg">Watch our promise</span>
            </button>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow mb-4">Why it matters</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest leading-[1.15]">Every family deserves food they never have to question.</h2>
            <p className="mt-6 text-sm sm:text-base text-mutedgreen leading-relaxed max-w-lg">
              Think of the moment you choose food for the people you love — reading the label, wondering where it came from, hoping it's as good as it claims to be. We believe that moment should feel calm, not uncertain.
            </p>
            <p className="mt-4 text-sm sm:text-base text-mutedgreen leading-relaxed max-w-lg">
              That quiet confidence is the foundation of everything we do — from selecting ingredients and working with farmers, to quality testing and packaging every product that carries the Nutribest name.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 3 — Make the promise memorable (Golden Rule) */}
      <section className="py-20 sm:py-28 border-t border-forest/10" data-testid="golden-rule">
        <div className="container-nb grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="eyebrow mb-5">Our Golden Rule</p>
            <blockquote className="font-serif text-3xl sm:text-4xl lg:text-[2.7rem] text-forest leading-[1.15]">
              <span className="text-gold">&ldquo;</span>If it isn't good enough for our own family, it isn't good enough for our customers.<span className="text-gold">&rdquo;</span>
            </blockquote>
            <p className="mt-6 text-sm sm:text-base text-mutedgreen leading-relaxed max-w-md">This isn't a marketing statement. It's the standard by which we make every decision.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-forest/10 bg-white p-8 sm:p-10 shadow-sm">
              <p className="eyebrow mb-4">The feeling we hope to create</p>
              <p className="text-sm sm:text-base text-mutedgreen leading-relaxed">When you choose Nutribest, we don't just want you to enjoy our products. We want you to feel <span className="font-serif italic text-forest">confident</span>.</p>
              <div className="relative overflow-hidden mt-6 rounded-2xl bg-forestdeep grain px-6 py-9 text-center">
                <p className="font-serif text-xl sm:text-2xl text-cream leading-snug">&ldquo;If it's from Nutribest,<br className="hidden sm:block" /> I don't need to worry about quality.&rdquo;</p>
              </div>
              <p className="mt-6 text-sm text-mutedgreen leading-relaxed">That's the experience we strive to create — every day, with every product.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 — Explain what you believe */}
      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="What we believe" title="It begins with nature. And continues with care." sub="Six pillars shape the Nutribest experience — long before a product ever reaches your home." />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map(({ icon: Icon, title, text }, i) => (
              <StaggerItem key={title}>
                <div data-testid={`experience-pillar-${i + 1}`} className="group h-full bg-white rounded-2xl border border-forest/10 p-7 hover:border-gold/40 hover:shadow-lg hover:shadow-forest/5 transition-all duration-500">
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 w-14 h-14 rounded-full bg-cream border border-forest/15 flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-cream transition-colors duration-500">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-forest">{title}</h3>
                  <p className="mt-2 text-sm text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 4 — Show how you act on those beliefs */}
      <section className="relative py-20 sm:py-28 bg-cream border-t border-forest/10 overflow-hidden" data-testid="how-we-act">
        <div className="container-nb">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">How we act</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest leading-tight">Care in every step.</h2>
              <p className="mt-5 text-sm sm:text-base text-mutedgreen leading-relaxed">Belief means little without proof. Here's how that care shows up in what we actually do — from the field to your pantry.</p>
            </div>
          </Reveal>
          <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="evidence-steps">
            {EVIDENCE.map(({ icon: Icon, q, a }, i) => (
              <StaggerItem key={q}>
                <div data-testid={`evidence-step-${i + 1}`} className="group h-full rounded-2xl border border-forest/10 bg-forestdeep p-6 hover:border-gold/40 hover:shadow-xl hover:shadow-forest/10 transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <span className="w-12 h-12 rounded-full bg-gold/15 text-goldlight flex items-center justify-center"><Icon size={20} strokeWidth={1.6} /></span>
                    <span className="font-mono text-xs text-cream/40">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-xl text-cream">{q}</h3>
                  <p className="mt-2 text-sm text-cream/65 leading-relaxed">{a}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <div className="mt-12 flex items-center gap-4">
              <img src={SEAL} alt="Nutribest Promise Seal" className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-full ring-2 ring-forest/10" />
              <img src={SEAL_GOLD} alt="Nutribest Trust Seal" className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-full ring-2 ring-forest/10" />
              <p className="text-mutedgreen text-xs sm:text-sm max-w-xs leading-relaxed">Backed by our Promise &amp; Trust seals — verified quality at every stage.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 — Close with reassurance */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <ParallaxImage src={img("heroFarm")} alt="" className="absolute inset-0" speed={12} />
        <div className="absolute inset-0 bg-forestdeep/80" />
        <div className="relative container-nb grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl text-cream leading-tight">Trust isn't claimed.<br />It's earned.</h2>
            <p className="mt-6 text-cream/80 text-sm sm:text-base leading-relaxed max-w-md">Your trust is the reason we exist, and the responsibility we carry in everything we do — every day, with every product.</p>
            <Link to="/collection" data-testid="experience-explore-products" className="group mt-9 inline-flex items-center gap-2.5 bg-cream text-forest text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold hover:text-cream transition-colors duration-300">
              Explore Our Products <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="space-y-3.5">
              {COMMITMENTS.map((t) => (
                <li key={t} className="flex items-center gap-3.5 text-cream/90 text-base"><span className="w-6 h-6 rounded-full bg-gold flex items-center justify-center shrink-0"><Check size={12} strokeWidth={3} className="text-white" /></span> {t}</li>
              ))}
            </ul>
            <p className="mt-8 font-serif italic text-goldlight text-lg">— The Nutribest Family</p>
          </Reveal>
        </div>
      </section>

      <TrustBar quote={"\u201CIf it\u2019s from Nutribest, I don\u2019t need to worry about quality.\u201D"} />

      <AnimatePresence>
        {showVideo && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="family-video-modal"
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowVideo(false)}
          >
            <button data-testid="family-video-close" onClick={() => setShowVideo(false)} aria-label="Close video" className="absolute top-5 right-5 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><X size={22} /></button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.3 }}
              className="w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video key={videoUrl} src={videoUrl} controls autoPlay playsInline className="w-full h-full bg-black" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
